import React, { ReactNode } from 'react';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';

export interface InfoModalButton {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  body: ReactNode;
  buttons?: InfoModalButton[];
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const InfoModal: React.FC<InfoModalProps> = ({
  isOpen,
  onClose,
  title,
  body,
  buttons = [{ label: 'Close', onClick: () => {}, variant: 'primary' }],
  maxWidth = 'lg',
}) => {
  // Alt+C closes. The buttons are caller-supplied, so there's no save action to
  // bind here — but claiming the layer keeps both chords off the surface below.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  const getMaxWidthClass = () => {
    switch (maxWidth) {
      case 'sm':
        return 'sm:max-w-sm';
      case 'md':
        return 'sm:max-w-md';
      case 'lg':
        return 'sm:max-w-lg';
      case 'xl':
        return 'sm:max-w-xl';
      case '2xl':
        return 'sm:max-w-2xl';
      default:
        return 'sm:max-w-lg';
    }
  };

  const getButtonClass = (variant?: string) => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
      case 'secondary':
        return 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500';
      case 'danger':
        return 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
    }
  };

  const handleButtonClick = (button: InfoModalButton) => {
    button.onClick();
    if (button.label === 'Close') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 z-[100] transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"
            onClick={onClose}
          ></div>
        </div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div
          ref={panelRef}
          style={panelStyle}
          className={`relative z-[101] inline-block align-bottom bg-cmp-modal rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle ${getMaxWidthClass()} sm:w-full`}
        >
          <ModalHeader title={title} onClose={onClose} dragHandleProps={dragHandleProps} />

          {/* Body */}
          <div className="px-4 pt-5 pb-4 sm:p-6">
            {body}
          </div>

          {/* Footer with buttons */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {buttons.map((button, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleButtonClick(button)}
                disabled={button.disabled}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  index > 0 ? 'sm:mr-3' : ''
                } sm:ml-3 sm:w-auto sm:text-sm ${getButtonClass(button.variant)} ${button.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {button.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;
